import { MarkerType, type Edge, type Node, Position } from "reactflow";
import type { DataMartDimension, DataMartFact, DataMartGraph } from "../model/types";
import type { DataMartNodeData } from "../components/types";

export type DataMartEdgeData = {
  relationshipType: "fact-dimension" | "dimension-dimension";
  relationshipId: number | null;
  columns: string[];
};

const FACT_HORIZONTAL_GAP = 620;
const DIMENSION_RADIUS = 320;
const SIDE_LANE_GAP = 200;
const NODE_WIDTH = 240;
const NODE_HEIGHT = 160;
const NODE_GAP_X = 80;
const NODE_GAP_Y = 80;
const CLUSTER_GAP_X = 280;
const CLUSTER_GAP_Y = 240;
const CLUSTER_MAX_ROW_WIDTH = 5200;

type LayoutMap = Map<string, { x: number; y: number }>;
type Point = { x: number; y: number };
type Rect = { left: number; right: number; top: number; bottom: number };

const createNode = (
  entity: DataMartFact | DataMartDimension,
  type: "factNode" | "dimensionNode",
  position: { x: number; y: number },
  onSelect: (id: string) => void
) =>
  ({
    id: entity.id,
    type,
    position,
    data: { entity, kind: entity.kind, onSelect },
    sourcePosition: Position.Right,
    targetPosition: Position.Left
  }) as Node<DataMartNodeData>;

const averagePlacement = (
  placements: Map<
    string,
    {
      x: number;
      y: number;
      count: number;
    }
  >,
  id: string,
  x: number,
  y: number
) => {
  const existing = placements.get(id);
  if (!existing) {
    placements.set(id, { x, y, count: 1 });
    return;
  }
  existing.x += x;
  existing.y += y;
  existing.count += 1;
};

const getPlacementResult = (
  placements: Map<string, { x: number; y: number; count: number }>,
  id: string
): { x: number; y: number } | null => {
  const placement = placements.get(id);
  if (!placement || placement.count === 0) {
    return null;
  }
  return {
    x: placement.x / placement.count,
    y: placement.y / placement.count
  };
};

const parseFactSideFromSourceHandle = (handleId?: string | null): Position | null => {
  if (!handleId) {
    return null;
  }
  const normalized = handleId.toLowerCase();
  if (normalized.includes("-left-")) {
    return Position.Left;
  }
  if (normalized.includes("-right-")) {
    return Position.Right;
  }
  if (normalized.includes("-top-")) {
    return Position.Top;
  }
  if (normalized.includes("-bottom-")) {
    return Position.Bottom;
  }
  return null;
};

const getLaneOffset = (index: number, total: number) => (index - (total - 1) / 2) * SIDE_LANE_GAP;

const toRect = (point: Point): Rect => ({
  left: point.x - NODE_WIDTH / 2,
  right: point.x + NODE_WIDTH / 2,
  top: point.y - NODE_HEIGHT / 2,
  bottom: point.y + NODE_HEIGHT / 2
});

const overlaps = (a: Rect, b: Rect) =>
  !(a.right + NODE_GAP_X <= b.left ||
    b.right + NODE_GAP_X <= a.left ||
    a.bottom + NODE_GAP_Y <= b.top ||
    b.bottom + NODE_GAP_Y <= a.top);

const placeWithoutOverlap = (desired: Point, occupied: Rect[]): Point => {
  const stepX = NODE_WIDTH + NODE_GAP_X;
  const stepY = NODE_HEIGHT + NODE_GAP_Y;
  const candidate = (x: number, y: number): Point => ({ x, y });
  const isFree = (point: Point) => {
    const rect = toRect(point);
    return occupied.every((used) => !overlaps(rect, used));
  };
  if (isFree(desired)) {
    return desired;
  }
  for (let ring = 1; ring <= 20; ring += 1) {
    for (let dx = -ring; dx <= ring; dx += 1) {
      const top = candidate(desired.x + dx * stepX, desired.y - ring * stepY);
      if (isFree(top)) {
        return top;
      }
      const bottom = candidate(desired.x + dx * stepX, desired.y + ring * stepY);
      if (isFree(bottom)) {
        return bottom;
      }
    }
    for (let dy = -ring + 1; dy <= ring - 1; dy += 1) {
      const left = candidate(desired.x - ring * stepX, desired.y + dy * stepY);
      if (isFree(left)) {
        return left;
      }
      const right = candidate(desired.x + ring * stepX, desired.y + dy * stepY);
      if (isFree(right)) {
        return right;
      }
    }
  }
  return desired;
};

const buildConnectedComponents = (graph: DataMartGraph): string[][] => {
  const nodeIds = [...graph.facts.map((fact) => fact.id), ...graph.dimensions.map((dimension) => dimension.id)];
  const adjacency = new Map<string, Set<string>>(nodeIds.map((id) => [id, new Set<string>()]));

  graph.relationships.forEach((relation) => {
    if (!adjacency.has(relation.sourceId) || !adjacency.has(relation.targetId)) {
      return;
    }
    adjacency.get(relation.sourceId)?.add(relation.targetId);
    adjacency.get(relation.targetId)?.add(relation.sourceId);
  });

  const components: string[][] = [];
  const visited = new Set<string>();

  nodeIds.forEach((startId) => {
    if (visited.has(startId)) {
      return;
    }
    const stack = [startId];
    const component: string[] = [];
    visited.add(startId);
    while (stack.length) {
      const current = stack.pop();
      if (!current) {
        continue;
      }
      component.push(current);
      adjacency.get(current)?.forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          stack.push(neighbor);
        }
      });
    }
    components.push(component.sort());
  });

  return components.sort((a, b) => b.length - a.length || a[0].localeCompare(b[0]));
};

const computeClusterLayout = (
  graph: DataMartGraph,
  componentNodeIds: Set<string>
): { positions: LayoutMap; bounds: Rect } => {
  const positions: LayoutMap = new Map();
  const occupied: Rect[] = [];
  const facts = graph.facts.filter((fact) => componentNodeIds.has(fact.id)).sort((a, b) => a.id.localeCompare(b.id));
  const dimensions = graph.dimensions
    .filter((dimension) => componentNodeIds.has(dimension.id))
    .sort((a, b) => a.id.localeCompare(b.id));
  const relationships = graph.relationships.filter(
    (relation) => componentNodeIds.has(relation.sourceId) && componentNodeIds.has(relation.targetId)
  );
  const dimensionPlacement = new Map<string, { x: number; y: number; count: number }>();

  facts.forEach((fact, index) => {
    const point = { x: index * FACT_HORIZONTAL_GAP, y: 0 };
    positions.set(fact.id, point);
    occupied.push(toRect(point));
  });

  facts.forEach((fact) => {
    const factPosition = positions.get(fact.id) ?? { x: 0, y: 0 };
    const connectedDimensions = relationships
      .filter((relation) => relation.type === "fact-dimension" && relation.sourceId === fact.id)
      .sort((a, b) => a.targetId.localeCompare(b.targetId));

    const bySide = {
      [Position.Left]: connectedDimensions.filter(
        (relation) => parseFactSideFromSourceHandle(relation.sourceHandle) === Position.Left
      ),
      [Position.Right]: connectedDimensions.filter(
        (relation) => parseFactSideFromSourceHandle(relation.sourceHandle) === Position.Right
      ),
      [Position.Top]: connectedDimensions.filter(
        (relation) => parseFactSideFromSourceHandle(relation.sourceHandle) === Position.Top
      ),
      [Position.Bottom]: connectedDimensions.filter(
        (relation) => parseFactSideFromSourceHandle(relation.sourceHandle) === Position.Bottom
      )
    };

    // Distribute dimensions that have NO explicit handle (the common case for migrated data, where
    // sourceHandle is null) radially around ALL FOUR sides of the fact, so a fact with many
    // dimensions forms a Kimball STAR rather than two tall left/right columns. Round-robin to the
    // currently-emptiest side keeps the star balanced.
    const sideOrder = [Position.Right, Position.Left, Position.Bottom, Position.Top];
    connectedDimensions.forEach((relation) => {
      const side = parseFactSideFromSourceHandle(relation.sourceHandle);
      if (side) {
        return;
      }
      const emptiest = sideOrder.reduce((best, candidate) =>
        bySide[candidate].length < bySide[best].length ? candidate : best
      );
      bySide[emptiest].push(relation);
    });

    bySide[Position.Left].forEach((relation, index) => {
      averagePlacement(
        dimensionPlacement,
        relation.targetId,
        factPosition.x - DIMENSION_RADIUS,
        factPosition.y + getLaneOffset(index, bySide[Position.Left].length)
      );
    });
    bySide[Position.Right].forEach((relation, index) => {
      averagePlacement(
        dimensionPlacement,
        relation.targetId,
        factPosition.x + DIMENSION_RADIUS,
        factPosition.y + getLaneOffset(index, bySide[Position.Right].length)
      );
    });
    bySide[Position.Top].forEach((relation, index) => {
      averagePlacement(
        dimensionPlacement,
        relation.targetId,
        factPosition.x + getLaneOffset(index, bySide[Position.Top].length),
        factPosition.y - DIMENSION_RADIUS
      );
    });
    bySide[Position.Bottom].forEach((relation, index) => {
      averagePlacement(
        dimensionPlacement,
        relation.targetId,
        factPosition.x + getLaneOffset(index, bySide[Position.Bottom].length),
        factPosition.y + DIMENSION_RADIUS
      );
    });
  });

  dimensions.forEach((dimension, index) => {
    const preferred = getPlacementResult(dimensionPlacement, dimension.id) ?? {
      x: facts.length * FACT_HORIZONTAL_GAP + (index % 3) * (NODE_WIDTH + NODE_GAP_X),
      y: Math.floor(index / 3) * (NODE_HEIGHT + NODE_GAP_Y)
    };
    const placed = placeWithoutOverlap(preferred, occupied);
    positions.set(dimension.id, placed);
    occupied.push(toRect(placed));
  });

  const allPositions = Array.from(positions.values());
  const minX = Math.min(...allPositions.map((position) => position.x - NODE_WIDTH / 2));
  const maxX = Math.max(...allPositions.map((position) => position.x + NODE_WIDTH / 2));
  const minY = Math.min(...allPositions.map((position) => position.y - NODE_HEIGHT / 2));
  const maxY = Math.max(...allPositions.map((position) => position.y + NODE_HEIGHT / 2));

  return {
    positions,
    bounds: { left: minX, right: maxX, top: minY, bottom: maxY }
  };
};

export const computeLayout = (graph: DataMartGraph): LayoutMap => {
  const globalLayout: LayoutMap = new Map();
  const components = buildConnectedComponents(graph).map((ids) => new Set(ids));

  // Split into structured clusters (a real star: >1 node, i.e. a fact with dimensions or a
  // snowflake chain) vs. UNATTACHED singletons (lone dimensions with no relationship in this mart
  // — common for conformed-dimension "common" marts that have no fact). Singletons get packed into
  // a tidy compact grid instead of being spread one-per-cluster across a very wide sparse row
  // (which made auto-arrange look like a no-op on fact-less marts).
  const clusters = components.filter((ids) => ids.size > 1);
  const singletons = components.filter((ids) => ids.size === 1);

  let cursorX = 0;
  let cursorY = 0;
  let currentRowHeight = 0;

  clusters.forEach((componentIds) => {
    const cluster = computeClusterLayout(graph, componentIds);
    const width = cluster.bounds.right - cluster.bounds.left;
    const height = cluster.bounds.bottom - cluster.bounds.top;

    if (cursorX > 0 && cursorX + width > CLUSTER_MAX_ROW_WIDTH) {
      cursorX = 0;
      cursorY += currentRowHeight + CLUSTER_GAP_Y;
      currentRowHeight = 0;
    }

    const offsetX = cursorX - cluster.bounds.left;
    const offsetY = cursorY - cluster.bounds.top;
    cluster.positions.forEach((position, nodeId) => {
      globalLayout.set(nodeId, { x: position.x + offsetX, y: position.y + offsetY });
    });

    cursorX += width + CLUSTER_GAP_X;
    currentRowHeight = Math.max(currentRowHeight, height);
  });

  // pack unattached singletons into a compact grid beneath the structured clusters (or filling the
  // canvas if there are none). Column count scales with sqrt for a roughly square block.
  if (singletons.length) {
    const ids = singletons
      .map((set) => Array.from(set)[0])
      .sort((a, b) => a.localeCompare(b));
    const cols = Math.max(1, Math.ceil(Math.sqrt(ids.length)));
    const stepX = NODE_WIDTH + NODE_GAP_X;
    const stepY = NODE_HEIGHT + NODE_GAP_Y;
    const startY = cursorX > 0 || cursorY > 0 ? cursorY + currentRowHeight + CLUSTER_GAP_Y : 0;
    ids.forEach((id, i) => {
      globalLayout.set(id, {
        x: (i % cols) * stepX,
        y: startY + Math.floor(i / cols) * stepY
      });
    });
  }

  return globalLayout;
};

export const buildFlowElements = (
  graph: DataMartGraph,
  previousNodes: Node<DataMartNodeData>[],
  onSelect: (id: string) => void
): { nodes: Node<DataMartNodeData>[]; edges: Edge<DataMartEdgeData>[] } => {
  const layout = computeLayout(graph);
  const previousPositions = new Map(previousNodes.map((node) => [node.id, node.position]));
  const persistedPositions = new Map(
    [...graph.facts, ...graph.dimensions]
      .filter(
        (entity) =>
          typeof entity.record.canvas_x === "number" && typeof entity.record.canvas_y === "number"
      )
      .map((entity) => [
        entity.id,
        {
          x: entity.record.canvas_x as number,
          y: entity.record.canvas_y as number
        }
      ])
  );

  const resolvePosition = (id: string) =>
    previousPositions.get(id) ?? persistedPositions.get(id) ?? layout.get(id) ?? { x: 0, y: 0 };

  const nodes: Node<DataMartNodeData>[] = [
    ...graph.facts.map((fact) =>
      createNode(fact, "factNode", resolvePosition(fact.id), onSelect)
    ),
    ...graph.dimensions.map((dimension) =>
      createNode(dimension, "dimensionNode", resolvePosition(dimension.id), onSelect)
    )
  ];

  const edges: Edge<DataMartEdgeData>[] = graph.relationships.map((relation) => ({
    id: relation.id,
    source: relation.sourceId,
    target: relation.targetId,
    sourceHandle: relation.sourceHandle ?? undefined,
    targetHandle: relation.targetHandle ?? undefined,
    type: "smoothstep",
    markerEnd: {
      type: MarkerType.ArrowClosed
    },
    data: {
      relationshipType: relation.type,
      relationshipId: relation.record?.target_entity_dm_relationship_id ?? null,
      columns: relation.columnNames
    },
    style: {
      stroke: relation.type === "fact-dimension" ? "#0891b2" : "#a855f7",
      strokeWidth: relation.type === "fact-dimension" ? 2.4 : 2,
      strokeDasharray: relation.type === "dimension-dimension" ? "6 4" : undefined
    }
  }));

  return { nodes, edges };
};
